import { useEffect, useRef } from 'react';

const useFilterScrollPositionManager = () => {
  const filtersModalRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const scrollAnchor = useRef<HTMLElement | null>(null);
  const prevScrollAnchorTop = useRef(0);

  function setScrollAnchor(element: HTMLElement | null) {
    // focus event and click event can both trigger, avoid reassigning if already set
    if (element === scrollAnchor.current) return;

    scrollAnchor.current = element;
    prevScrollAnchorTop.current = element?.getBoundingClientRect().top || 0;
  }

  function clearScrollAnchor() {
    scrollAnchor.current = null;
    prevScrollAnchorTop.current = 0;
  }

  const handleClick = ({ target }: MouseEvent) => {
    if (!(target instanceof Element) || target instanceof HTMLInputElement)
      return;
    if (target.classList.contains('ch-checkbox__label')) {
      setScrollAnchor(target.closest('li'));
    } else {
      clearScrollAnchor();
    }
  };

  const handleFocusIn = ({ target }: FocusEvent) => {
    if (
      !(target instanceof Element) ||
      !(target instanceof HTMLInputElement) ||
      !target.parentElement
    )
      return;

    if (
      target.parentElement.classList.contains(
        'ac-instant-search__filter-option'
      )
    ) {
      setScrollAnchor(target.closest('li'));
    } else {
      clearScrollAnchor();
    }
  };

  useEffect(() => {
    const filters = filtersRef.current;
    const filtersModal = filtersModalRef.current;
    if (!filters || !filtersModal) return;

    filters.addEventListener('click', handleClick);
    filters.addEventListener('focusin', handleFocusIn);

    const resizeObserver = new ResizeObserver(() => {
      if (!scrollAnchor.current) return;

      const newScrollTop =
        scrollAnchor.current?.getBoundingClientRect().top || 0;

      const diff = newScrollTop - prevScrollAnchorTop.current;

      if (diff !== 0) {
        filtersModal.scrollTop += diff;
      }
    });

    resizeObserver.observe(filters);

    return () => {
      filters.removeEventListener('click', handleClick);
      filters.removeEventListener('focusin', handleFocusIn);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    filtersModalRef,
    filtersRef,
  };
};

export default useFilterScrollPositionManager;
